import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PrinterIcon,
  CalendarIcon,
  UsersIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Booking } from '../../types';
import {
  bookingMatchesDepartureDate,
  compareBookingsByHailDepartureOldestFirst,
  getBookingHailDepartureGregorian,
} from '../../utils/bookingDeparture';
import { compareGregorianByHijriCalendar } from '../../components/Forms/hijriDate';
import { toGregorianDateOnly } from '../../utils/dateDisplay';
import { DateInput, DateDisplay } from '../../components/Forms';
import BookingForm from './BookingForm';
import BookingDetails from './BookingDetails';
import Invoice from '../../components/Invoice/Invoice';

interface BookingFilters {
  search: string;
  status: string;
  paymentStatus: string;
  tripType: string;
  departureDate: string;
}

const countBookingPassengers = (booking: Booking): number =>
  1 + (booking.companions?.length || 0);

const countPassengersInBookings = (list: Booking[]): number =>
  list.reduce((sum, booking) => sum + countBookingPassengers(booking), 0);

const BOOKINGS_PAGE_SIZE = 20;

const Bookings: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<BookingFilters>({
    search: '',
    status: '',
    paymentStatus: '',
    tripType: '',
    departureDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [invoiceBooking, setInvoiceBooking] = useState<Booking | null>(null);

  const queryClient = useQueryClient();

  const isDateFiltered = Boolean(filters.departureDate);

  const apiFilters = useMemo(
    () => ({
      search: filters.search,
      status: filters.status,
      paymentStatus: filters.paymentStatus,
      tripType: filters.tripType,
    }),
    [filters.search, filters.status, filters.paymentStatus, filters.tripType]
  );

  // جلب كل الحجوزات بنفس حجم الصفحة دائماً — الفلترة حسب التاريخ على الواجهة
  const { 
    data: bookingsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['bookings', 'all', apiFilters],
    queryFn: async () => {
      let page = 1;
      let allBookings: Booking[] = [];
      let serverPagination = {
        current: 1,
        limit: BOOKINGS_PAGE_SIZE,
        total: 0,
        pages: 1,
      };

      while (page <= 50) {
        const response = await apiService.getBookings(
          page,
          BOOKINGS_PAGE_SIZE,
          apiFilters
        );
        allBookings = [...allBookings, ...(response.data ?? [])];
        serverPagination = response.pagination ?? serverPagination;
        if (!response.pagination || page >= response.pagination.pages) break;
        page += 1;
      }

      return {
        success: true,
        data: allBookings,
        pagination: serverPagination,
      };
    },
    staleTime: 30000, // 30 seconds
  });

  const pagination = bookingsData?.pagination;

  const allBookings = useMemo(() => {
    const data = bookingsData?.data ?? [];
    return [...data].sort(compareBookingsByHailDepartureOldestFirst);
  }, [bookingsData?.data]);

  const bookings = useMemo(() => {
    if (!filters.departureDate) return allBookings;
    return allBookings.filter((booking) =>
      bookingMatchesDepartureDate(booking, filters.departureDate)
    );
  }, [allBookings, filters.departureDate]);

  // مصدر العرض الكامل — الفلترة تعمل على كل البيانات
  const displayBookings = useMemo(
    () => (isDateFiltered ? bookings : allBookings),
    [isDateFiltered, bookings, allBookings]
  );

  const clientPagination = useMemo(() => {
    const total = displayBookings.length;
    const pages = Math.max(1, Math.ceil(total / BOOKINGS_PAGE_SIZE));
    const current = Math.min(currentPage, pages);
    return {
      total,
      pages,
      current,
      from: total === 0 ? 0 : (current - 1) * BOOKINGS_PAGE_SIZE + 1,
      to: Math.min(current * BOOKINGS_PAGE_SIZE, total),
    };
  }, [displayBookings.length, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.departureDate,
    filters.search,
    filters.status,
    filters.paymentStatus,
    filters.tripType,
  ]);

  useEffect(() => {
    if (currentPage > clientPagination.pages) {
      setCurrentPage(clientPagination.pages);
    }
  }, [clientPagination.pages, currentPage]);

  const paginatedBookings = useMemo(() => {
    const start = (clientPagination.current - 1) * BOOKINGS_PAGE_SIZE;
    return displayBookings.slice(start, start + BOOKINGS_PAGE_SIZE);
  }, [displayBookings, clientPagination.current]);

  // إحصائيات كاملة لكل تاريخ (للعناوين) — من كل النتائج المفلترة
  const bookingsByDateFull = useMemo(() => {
    const grouped = new Map<string, Booking[]>();
    for (const booking of displayBookings) {
      const dateKey = getBookingHailDepartureGregorian(booking) || 'unknown';
      const list = grouped.get(dateKey) ?? [];
      list.push(booking);
      grouped.set(dateKey, list);
    }
    return grouped;
  }, [displayBookings]);

  const bookingsByDatePage = useMemo(() => {
    const grouped = new Map<string, Booking[]>();
    for (const booking of paginatedBookings) {
      const dateKey = getBookingHailDepartureGregorian(booking) || 'unknown';
      const list = grouped.get(dateKey) ?? [];
      list.push(booking);
      grouped.set(dateKey, list);
    }
    return Array.from(grouped.entries()).sort(([dateA], [dateB]) => {
      if (dateA === 'unknown') return 1;
      if (dateB === 'unknown') return -1;
      return compareGregorianByHijriCalendar(dateB, dateA);
    });
  }, [paginatedBookings]);

  const filteredDateSummary = useMemo(() => {
    if (!filters.departureDate) return null;
    return {
      bookingsCount: bookings.length,
      passengersCount: countPassengersInBookings(bookings),
    };
  }, [filters.departureDate, bookings]);

  // Delete booking mutation
  const deleteBookingMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteBooking(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof BookingFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDepartureDateChange = (value: string) => {
    const normalized = toGregorianDateOnly(value) || value.trim();
    setFilters((prev) => ({ ...prev, departureDate: normalized }));
    setCurrentPage(1);
  };

  const clearDepartureDate = () => {
    handleDepartureDateChange('');
  };

  const handleDeleteBooking = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الحجز؟')) {
      try {
        await deleteBookingMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting booking:', error);
        alert('حدث خطأ أثناء حذف الحجز');
      }
    }
  };

  const handleCreateBooking = () => {
    setSelectedBooking(null);
    setShowBookingForm(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowBookingForm(true);
  };

  const handleViewBooking = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setShowBookingDetails(true);
  };

  const handleBookingFormClose = () => {
    setShowBookingForm(false);
    setSelectedBooking(null);
  };

  const handleBookingDetailsClose = () => {
    setShowBookingDetails(false);
    setSelectedBookingId(null);
  };

  const handleBookingSuccess = (booking: Booking) => {
    // Optionally show success message or redirect
    console.log('Booking saved successfully:', booking);
  };

  const handlePrintInvoice = (booking: Booking) => {
    setInvoiceBooking(booking);
    setShowInvoice(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setInvoiceBooking(null);
  };

  const handlePrint = () => {
    // Ensure the invoice modal is visible before printing
    const invoiceModal = document.querySelector('.invoice-modal-print');
    if (invoiceModal) {
      // Force the modal to be visible
      (invoiceModal as HTMLElement).style.display = 'block';
      (invoiceModal as HTMLElement).style.visibility = 'visible';
    }
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'pending': { label: 'في انتظار التأكيد', color: 'bg-yellow-100 text-yellow-800' },
      'confirmed': { label: 'مؤكد', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'ملغي', color: 'bg-red-100 text-red-800' },
      'completed': { label: 'مكتمل', color: 'bg-blue-100 text-blue-800' },
      'refunded': { label: 'مسترد', color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusMap = {
      'pending': { label: 'في انتظار الدفع', color: 'bg-red-100 text-red-800', icon: ClockIcon },
      'partial': { label: 'مدفوع جزئياً', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      'paid': { label: 'مدفوع بالكامل', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      'refunded': { label: 'مسترد', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800', 
      icon: ClockIcon 
    };
    
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusInfo.label}
      </span>
    );
  };

  const getTripTypeText = (type: string) => {
    const typeMap = {
      'hajj': 'حج',
      'umrah': 'عمرة',
      'transport': 'نقل'
    };
    return typeMap[type as keyof typeof typeMap] || type;
  };

  const renderBookingRow = (booking: Booking) => (
    <tr key={booking._id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {booking.bookingNumber}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {booking.contactPerson.name}
        </div>
        <div className="text-sm text-gray-500">
          {booking.contactPerson.phone}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getTripTypeText(booking.tripType)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {getBookingRouteInfo(booking).to}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {countBookingPassengers(booking)} شخص
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {formatCurrency(booking.pricing.totalAmount)}
        </div>
        {booking.pricing.remainingAmount > 0 && (
          <div className="text-sm text-red-600">
            متبقي: {formatCurrency(booking.pricing.remainingAmount)}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {getPaymentStatusBadge(booking.paymentStatus)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {getStatusBadge(booking.status)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex items-center space-x-2 space-x-reverse">
          <button
            className="text-blue-600 hover:text-blue-900"
            title="عرض التفاصيل"
            onClick={() => handleViewBooking(booking._id)}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            className="text-purple-600 hover:text-purple-900"
            title="طباعة الفاتورة"
            onClick={() => handlePrintInvoice(booking)}
          >
            <PrinterIcon className="w-4 h-4" />
          </button>
          <button
            className="text-green-600 hover:text-green-900"
            title="تعديل"
            onClick={() => handleEditBooking(booking)}
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            className="text-red-600 hover:text-red-900"
            title="حذف"
            onClick={() => handleDeleteBooking(booking._id)}
            disabled={deleteBookingMutation.isPending}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  // Helper function to get booking route info
  const getBookingRouteInfo = (booking: Booking) => {
    // If booking has new routes format, use the first active route
    if (booking.routes && booking.routes.length > 0) {
      const activeRoute = booking.routes.find(r => r.isActive) || booking.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDate: new Date(`${activeRoute.dateFrom}T${activeRoute.timeFrom}`),
        returnDate: new Date(`${activeRoute.dateTo}T${activeRoute.timeTo}`)
      };
    }
    
    // Fallback to legacy route format
    if (booking.route) {
      return {
        from: booking.route.from,
        to: booking.route.to,
        departureDate: booking.route.departureDate,
        returnDate: booking.route.returnDate
      };
    }
    
    // Default fallback
    return {
      from: 'غير محدد',
      to: 'غير محدد',
      departureDate: new Date(),
      returnDate: new Date()
    };
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">خطأ في تحميل البيانات</h3>
          <p className="text-red-600 mt-1">حدث خطأ أثناء تحميل بيانات الحجوزات</p>
          <button 
            onClick={() => refetch()}
            className="mt-3 bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة الحجوزات</h1>
          <p className="mt-1 text-sm text-gray-500">
            إجمالي الحجوزات: {pagination?.total || allBookings.length}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button onClick={handleCreateBooking} className="btn-primary">
            <PlusIcon className="w-5 h-5 ml-2" />
            حجز جديد
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الحجز، الاسم، أو رقم الجوال..."
                className="input-field pr-10"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary ${filters.departureDate ? 'ring-2 ring-primary-300' : ''}`}
          >
            <FunnelIcon className="w-5 h-5 ml-2" />
            فلترة
            {filters.departureDate && (
              <span className="mr-2 inline-flex h-2 w-2 rounded-full bg-primary-500" />
            )}
          </button>
        </div>

        {filteredDateSummary && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-lg bg-primary-50 border border-primary-100 px-4 py-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-primary-900">
                <CalendarIcon className="w-5 h-5" />
                <DateDisplay value={filters.departureDate} dualLayout="inline" />
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-800">
                <UsersIcon className="w-5 h-5" />
                <span>
                  <span className="font-semibold">{filteredDateSummary.passengersCount}</span> مسافر
                </span>
                <span className="text-primary-300">|</span>
                <span>
                  <span className="font-semibold">{filteredDateSummary.bookingsCount}</span> حجز
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={clearDepartureDate}
              className="text-sm text-primary-700 hover:text-primary-900 font-medium flex items-center"
            >
              <XMarkIcon className="w-4 h-4 ml-1" />
              عرض كل التواريخ
            </button>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حالة الحجز
                </label>
                <select
                  className="input-field"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="pending">في انتظار التأكيد</option>
                  <option value="confirmed">مؤكد</option>
                  <option value="cancelled">ملغي</option>
                  <option value="completed">مكتمل</option>
                  <option value="refunded">مسترد</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حالة الدفع
                </label>
                <select
                  className="input-field"
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                >
                  <option value="">جميع حالات الدفع</option>
                  <option value="pending">في انتظار الدفع</option>
                  <option value="partial">مدفوع جزئياً</option>
                  <option value="paid">مدفوع بالكامل</option>
                  <option value="refunded">مسترد</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نوع الرحلة
                </label>
                <select
                  className="input-field"
                  value={filters.tripType}
                  onChange={(e) => handleFilterChange('tripType', e.target.value)}
                >
                  <option value="">جميع الأنواع</option>
                  <option value="hajj">حج</option>
                  <option value="umrah">عمرة</option>
                  <option value="transport">نقل</option>
                </select>
              </div>

              <div>
                <DateInput
                  label="تاريخ المغادرة"
                  compactToggle
                  value={filters.departureDate}
                  onChange={(e) => handleDepartureDateChange(e.target.value)}
                />
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Bookings Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري تحميل الحجوزات...</p>
          </div>
        ) : displayBookings.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">
              {filters.departureDate
                ? 'لا توجد حجوزات في تاريخ المغادرة المحدد'
                : 'لا توجد حجوزات'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      رقم الحجز
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      العميل
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      نوع الرحلة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الوجهة
                    </th>
                    
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      عدد الأشخاص
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المبلغ الإجمالي
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      حالة الدفع
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      حالة الحجز
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isDateFiltered
                    ? paginatedBookings.map(renderBookingRow)
                    : bookingsByDatePage.map(([dateKey, dateBookingsOnPage]) => {
                        const fullDateBookings =
                          bookingsByDateFull.get(dateKey) ?? dateBookingsOnPage;
                        return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-50 border-y border-gray-200">
                            <td colSpan={9} className="px-6 py-3">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                  <CalendarIcon className="w-5 h-5 text-primary-600 shrink-0" />
                                  {dateKey === 'unknown' ? (
                                    <span className="text-sm font-semibold text-gray-700">
                                      بدون تاريخ مغادرة
                                    </span>
                                  ) : (
                                    <DateDisplay
                                      value={dateKey}
                                      dualLayout="inline"
                                      gregorianClassName="text-sm font-semibold text-gray-900"
                                      hijriClassName="text-sm text-gray-600"
                                    />
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  <span>
                                    <span className="font-semibold text-gray-900">
                                      {countPassengersInBookings(fullDateBookings)}
                                    </span>{' '}
                                    مسافر
                                  </span>
                                  <span>
                                    <span className="font-semibold text-gray-900">
                                      {fullDateBookings.length}
                                    </span>{' '}
                                    حجز
                                  </span>
                                  {dateKey !== 'unknown' && (
                                    <button
                                      type="button"
                                      onClick={() => handleDepartureDateChange(dateKey)}
                                      className="text-primary-600 hover:text-primary-800 font-medium"
                                    >
                                      عرض هذا التاريخ
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                          {dateBookingsOnPage.map(renderBookingRow)}
                        </React.Fragment>
                        );
                      })}
                </tbody>
              </table>
            </div>

            {clientPagination.pages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(Math.max(1, clientPagination.current - 1))}
                      disabled={clientPagination.current === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      السابق
                    </button>
                    <button
                      onClick={() =>
                        handlePageChange(
                          Math.min(clientPagination.pages, clientPagination.current + 1)
                        )
                      }
                      disabled={clientPagination.current === clientPagination.pages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      التالي
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        عرض{' '}
                        <span className="font-medium">{clientPagination.from}</span> إلى{' '}
                        <span className="font-medium">{clientPagination.to}</span> من{' '}
                        <span className="font-medium">{clientPagination.total}</span> نتيجة
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => handlePageChange(Math.max(1, clientPagination.current - 1))}
                          disabled={clientPagination.current === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          السابق
                        </button>

                        {Array.from(
                          { length: Math.min(5, clientPagination.pages) },
                          (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  clientPagination.current === pageNum
                                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}

                        <button
                          onClick={() =>
                            handlePageChange(
                              Math.min(clientPagination.pages, clientPagination.current + 1)
                            )
                          }
                          disabled={clientPagination.current === clientPagination.pages}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          التالي
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Booking Form Modal */}
      <BookingForm
        booking={selectedBooking || undefined}
        isOpen={showBookingForm}
        onClose={handleBookingFormClose}
        onSuccess={handleBookingSuccess}
      />

      {/* Booking Details Modal */}
      {selectedBookingId && (
        <BookingDetails
          bookingId={selectedBookingId}
          isOpen={showBookingDetails}
          onClose={handleBookingDetailsClose}
          onEdit={handleEditBooking}
        />
      )}

      {/* Invoice Modal — portal to body so print hides the rest of the app */}
      {showInvoice && invoiceBooking && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto invoice-modal-print">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0 print:min-h-0 print:p-0 print:block print:w-full print:text-right">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 print:hidden"
              onClick={handleCloseInvoice}
              aria-hidden
            />

            <div className="inline-block w-full max-w-6xl my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg print:block print:w-full print:max-w-none print:my-0 print:shadow-none print:rounded-none print:overflow-visible">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden">
                <h2 className="text-xl font-bold text-gray-900">
                  فاتورة الحجز - {invoiceBooking.bookingNumber}
                </h2>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <button
                    onClick={handlePrint}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <PrinterIcon className="w-4 h-4 ml-2" />
                    طباعة
                  </button>
                  <button
                    onClick={handleCloseInvoice}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-4 print:p-0">
                <Invoice booking={invoiceBooking} />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Bookings; 